import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit as qLimit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';
import { toIsoString } from '../schemas/common';
import { parseDocs } from './parseDocs';
import {
  computeAudienceKeys,
  forumPostInputSchema,
  forumPostSchema,
  type ForumPost,
  type ForumPostInput,
} from '../schemas/forumPost';

const COLLECTION = 'forumPosts';

function db() {
  return getFirebaseClients().firestore;
}

function parse(raw: unknown, id: string): ForumPost {
  const data = raw as Record<string, unknown>;
  return forumPostSchema.parse({
    ...data,
    id,
    createdAt: toIsoString(data.createdAt),
    updatedAt: toIsoString(data.updatedAt),
  });
}

/** Pinned first, then newest first. */
function sortFeed(a: ForumPost, b: ForumPost): number {
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
  return b.createdAt.localeCompare(a.createdAt);
}

/** Admin: every post, newest first. */
export async function listAllForumPosts(): Promise<ForumPost[]> {
  const q = query(collection(db(), COLLECTION), orderBy('createdAt', 'desc'), qLimit(500));
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION).sort(sortFeed);
}

/**
 * Public feed: 'Grand public' posts visible to EVERYONE (incl. anonymous
 * visitors). Used on the Publications page for non-members.
 */
export async function listPublicForumPosts(): Promise<ForumPost[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('published', '==', true),
    where('audienceKeys', 'array-contains', 'public'),
    orderBy('createdAt', 'desc'),
    qLimit(200),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION).sort(sortFeed);
}

/**
 * Member feed: everything an enrolled member may see — 'public' broadcasts,
 * 'all'-member posts, and posts for their formations. Pass the member's
 * `enrolledFormationIds`; we query `['public','all', ...those]` (capped at 10
 * for Firestore's array-contains-any limit), pinned-first client-side.
 * Returns [] when the member has no enrollments (use listPublicForumPosts then).
 */
export async function listForumPostsForMember(
  enrolledFormationIds: string[],
): Promise<ForumPost[]> {
  if (!enrolledFormationIds || enrolledFormationIds.length === 0) {
    return [];
  }
  const keys = ['public', 'all', ...enrolledFormationIds].slice(0, 10);
  const q = query(
    collection(db(), COLLECTION),
    where('published', '==', true),
    where('audienceKeys', 'array-contains-any', keys),
    orderBy('createdAt', 'desc'),
    qLimit(200),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION).sort(sortFeed);
}

export async function getForumPost(id: string): Promise<ForumPost | null> {
  const snap = await getDoc(doc(db(), COLLECTION, id));
  if (!snap.exists()) return null;
  return parse(snap.data(), snap.id);
}

export async function createForumPost(
  input: ForumPostInput & { authorUid?: string | null; authorName?: string },
): Promise<string> {
  const { authorUid, authorName, ...rest } = input;
  const parsed = forumPostInputSchema.parse(rest);
  const audienceKeys = computeAudienceKeys(parsed.audience, parsed.formationIds);
  const now = new Date().toISOString();
  const ref = await addDoc(collection(db(), COLLECTION), {
    ...parsed,
    audienceKeys,
    authorUid: authorUid ?? null,
    ...(authorName ? { authorName } : {}),
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updateForumPost(
  id: string,
  patch: Partial<ForumPostInput>,
): Promise<void> {
  // Keep audienceKeys in sync whenever audience/formationIds change.
  const next: Record<string, unknown> = { ...patch, updatedAt: serverTimestamp() };
  if (patch.audience !== undefined || patch.formationIds !== undefined) {
    const current = await getForumPost(id);
    const audience = patch.audience ?? current?.audience ?? 'all';
    const formationIds = patch.formationIds ?? current?.formationIds ?? [];
    next.audienceKeys = computeAudienceKeys(audience, formationIds);
  }
  await updateDoc(doc(db(), COLLECTION, id), next);
}

export async function deleteForumPost(id: string): Promise<void> {
  await deleteDoc(doc(db(), COLLECTION, id));
}
